// ============================================
// Nedap AtWork Hooks - TanStack Query
// ============================================
//
// Door access control qua backend trung gian. Xem
// docs/nedap_atwork_client_handoff.md.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { isApiError } from '@conai/api-client';
import { ATWORK_ERROR_MESSAGES } from '@conai/shared-constants';
import type {
  AtWorkEventListParams,
  AtWorkDailyStatsParams,
  AtWorkRecentDeniedParams,
  AtWorkDoorCommandListParams,
  ProvideAccessRequest,
  ProvideAccessResult,
} from '@conai/shared-types';
import { useAtWorkService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';

// ============================================
// Error mapping
// ============================================

interface AtWorkErrorInfo {
  status?: number;
  errorCode?: string;
  /** message thân thiện đã map (i18n đơn giản) */
  message: string;
}

/**
 * Bóc tách lỗi provide-access từ ApiClientError.
 * - 403: không đủ quyền mở cửa
 * - 503: tích hợp AtWork đang tắt
 * - 502: Nedap từ chối — đọc `errors.errorCode` từ envelope gốc để map message
 */
export function getAtWorkErrorInfo(error: unknown): AtWorkErrorInfo {
  if (!isApiError(error)) {
    return { message: 'Đã xảy ra lỗi không xác định. Vui lòng thử lại.' };
  }

  const status = error.status;

  // Đọc envelope gốc của backend: { data, code, message, errors: { errorCode } }
  const rawData = (error.originalError as { response?: { data?: unknown } } | undefined)
    ?.response?.data as
    | { message?: string; errors?: { errorCode?: string } }
    | undefined;
  const errorCode = rawData?.errors?.errorCode;

  if (status === 403) {
    return { status, message: 'Bạn không có quyền mở cửa này.' };
  }
  if (status === 503) {
    return { status, message: 'Tích hợp AtWork đang tạm tắt. Vui lòng thử lại sau.' };
  }
  if (errorCode) {
    return {
      status,
      errorCode,
      message: ATWORK_ERROR_MESSAGES[errorCode] ?? rawData?.message ?? error.message,
    };
  }
  return { status, message: rawData?.message ?? error.message };
}

// ============================================
// Doors
// ============================================

/** Danh sách cửa AtWork (đã lọc theo store scope của user ở backend) */
export function useAtWorkDoors() {
  const atWorkService = useAtWorkService();

  return useQuery({
    queryKey: queryKeys.atWork.doors(),
    queryFn: () => atWorkService.listDoors(),
  });
}

/** Lịch sử event theo cửa (mục 3.3) */
export function useAtWorkDoorEvents(
  doorId: string | undefined,
  params: AtWorkEventListParams = {},
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  const atWorkService = useAtWorkService();

  return useQuery({
    queryKey: queryKeys.atWork.events(doorId ?? '', params as Record<string, unknown>),
    queryFn: () => atWorkService.listDoorEvents(doorId!, params),
    enabled: options?.enabled !== false && !!doorId,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Mở cửa tạm thời (provide-access). Mặc định source = ADMIN_WEB ở backend.
 * Tự map lỗi 403/503/502 ra message thân thiện và hiển thị toast.
 */
export function useProvideAccess() {
  const atWorkService = useAtWorkService();
  const queryClient = useQueryClient();

  return useMutation<
    ProvideAccessResult,
    unknown,
    { doorId: string; data?: ProvideAccessRequest }
  >({
    mutationFn: ({ doorId, data }) => atWorkService.provideAccess(doorId, data),
    onSuccess: (result, variables) => {
      toast.success(result.message || 'Đã gửi lệnh mở cửa');
      // Làm mới event + audit log của cửa đó
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.atWork.all, 'events', variables.doorId],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.atWork.all, 'commands'],
      });
    },
    onError: (error) => {
      toast.error(getAtWorkErrorInfo(error).message);
    },
  });
}

// ============================================
// Stats & denied
// ============================================

/** Thống kê lượt mở theo ngày (mục 3.4) */
export function useAtWorkDailyStats(params: AtWorkDailyStatsParams = {}) {
  const atWorkService = useAtWorkService();

  return useQuery({
    queryKey: queryKeys.atWork.dailyStats(params as Record<string, unknown>),
    queryFn: () => atWorkService.getDailyAccessStats(params),
  });
}

/** Danh sách denied gần đây (mục 3.5) */
export function useAtWorkRecentDenied(params: AtWorkRecentDeniedParams = {}) {
  const atWorkService = useAtWorkService();

  return useQuery({
    queryKey: queryKeys.atWork.recentDenied(params as Record<string, unknown>),
    queryFn: () => atWorkService.getRecentDenied(params),
  });
}

// ============================================
// Audit log
// ============================================

/** Audit log mở cửa remote (mục 3.6) */
export function useAtWorkDoorCommands(params: AtWorkDoorCommandListParams = {}) {
  const atWorkService = useAtWorkService();

  return useQuery({
    queryKey: queryKeys.atWork.commands(params as Record<string, unknown>),
    queryFn: () => atWorkService.listDoorCommands(params),
  });
}

// ============================================
// Health
// ============================================

/**
 * Kiểm tra kết nối tới Nedap AtWork. Poll định kỳ để badge cập nhật.
 * Mặc định 30s; truyền `refetchInterval` để đổi.
 */
export function useAtWorkHealth(options?: { refetchInterval?: number }) {
  const atWorkService = useAtWorkService();

  return useQuery({
    queryKey: queryKeys.atWork.health(),
    queryFn: () => atWorkService.getHealth(),
    refetchInterval: options?.refetchInterval ?? 30000,
    retry: false,
  });
}
